import { isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';
import PQueue from 'p-queue';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { AppState } from '@/store/store.ts';
import { useCheckTargetAccumulatorMutation } from '@/store/targetAccumulator/api/index.ts';
import { targetAccumulatorErrorOccurred } from '@/store/targetAccumulator/slice.ts';
import type { FxTargetAccumulator, TargetAccumulatorProperties } from '@/store/targetAccumulator/state.ts';

// ██████╗  █████╗ ████████╗ █████╗
// ██╔══██╗██╔══██╗╚══██╔══╝██╔══██╗
// ██║  ██║███████║   ██║   ███████║
// ██║  ██║██╔══██║   ██║   ██╔══██║
// ██████╔╝██║  ██║   ██║   ██║  ██║
// ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝

export type Nullable<T> = {
  [P in keyof T]: T[P] extends string | number | boolean | undefined ? T[P] | null : T[P];
};

export type NullableTargetAccumulatorProperties = Partial<Nullable<TargetAccumulatorProperties>>;

export type TargetAccumulatorContextContent = {
  tileId: string;
  isChecking: boolean;
  checkTargetAccumulator: (change: NullableTargetAccumulatorProperties) => Promise<void>;
};

type TargetAccumulatorActionContextContent = Omit<TargetAccumulatorContextContent, 'tileId'>;

export const TargetAccumulatorContext = createContext<TargetAccumulatorContextContent>({
  tileId: '',
  isChecking: false,
  checkTargetAccumulator: async (_change: NullableTargetAccumulatorProperties) => {},
});

const TargetAccumulatorTileIdContext = createContext('');

const TargetAccumulatorActionContext = createContext<TargetAccumulatorActionContextContent>({
  isChecking: false,
  checkTargetAccumulator: async (_change: NullableTargetAccumulatorProperties) => {},
});

// ██████╗ ██████╗  ██████╗ ██╗   ██╗██╗██████╗ ███████╗██████╗
// ██╔══██╗██╔══██╗██╔═══██╗██║   ██║██║██╔══██╗██╔════╝██╔══██╗
// ██████╔╝██████╔╝██║   ██║██║   ██║██║██║  ██║█████╗  ██████╔╝
// ██╔═══╝ ██╔══██╗██║   ██║╚██╗ ██╔╝██║██║  ██║██╔══╝  ██╔══██╗
// ██║     ██║  ██║╚██████╔╝ ╚████╔╝ ██║██████╔╝███████╗██║  ██║
// ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═══╝  ╚═╝╚═════╝ ╚══════╝╚═╝  ╚═╝

export function TargetAccumulatorProvider(props: { tileId: string; children: ReactNode }) {
  const { tileId, children } = props;

  const dispatch = useAppDispatch();

  const [isChecking, setIsChecking] = useState(false);

  const { hasTargetAccumulator, currencyPair, accuType } = useAppSelector((state) => {
    const targetAccumulator = state.targetAccumulator[tileId];

    return {
      hasTargetAccumulator: isNotDefined(targetAccumulator) === false,
      currencyPair: targetAccumulator?.properties?.currencyPair,
      accuType: targetAccumulator?.properties?.accuType,
    };
  }, deepEqual);

  // Queue with concurrency=1 to ensure sequential execution
  // Only one checkTargetAccumulatorWithTradeCapture request can run at a time
  const queue = useMemo(() => new PQueue({ concurrency: 1 }), []);

  const [checkTargetAccumulatorByTradeCapture] = useCheckTargetAccumulatorMutation({
    selectFromResult: () => ({}),
  });

  const checkTargetAccumulator = useCallback(
    async (changes: NullableTargetAccumulatorProperties) => {
      // Add the call to the queue
      // If a request is already running, this one will wait until it completes
      return queue.add(async () => {
        if (!hasTargetAccumulator) {
          return;
        }

        // Timer to show "checking" state after 500ms
        const checkTimer = setTimeout(() => {
          setIsChecking(true);
        }, 500);

        try {
          await checkTargetAccumulatorByTradeCapture({
            tileId,
            properties: changes,
            currencyPair,
            accuType,
          })
            .unwrap()
            .catch(() => {
              dispatch(targetAccumulatorErrorOccurred({ tileId }));
            });
        } finally {
          // Clean up timer and state, even on error
          clearTimeout(checkTimer);
          setIsChecking(false);
        }
      });
    },
    [accuType, checkTargetAccumulatorByTradeCapture, currencyPair, hasTargetAccumulator, queue, tileId],
  );

  if (!hasTargetAccumulator) {
    throw new Error(`Could not find targetAccumulator with tileId ${tileId}`);
  }

  const actionContent: TargetAccumulatorActionContextContent = useMemo(
    () => ({
      isChecking,
      checkTargetAccumulator,
    }),
    [isChecking, checkTargetAccumulator],
  );

  const content: TargetAccumulatorContextContent = useMemo(
    () => ({
      tileId,
      ...actionContent,
    }),
    [actionContent, tileId],
  );

  return (
    <TargetAccumulatorTileIdContext.Provider value={tileId}>
      <TargetAccumulatorActionContext.Provider value={actionContent}>
        <TargetAccumulatorContext.Provider value={content}>{children}</TargetAccumulatorContext.Provider>
      </TargetAccumulatorActionContext.Provider>
    </TargetAccumulatorTileIdContext.Provider>
  );
}

// ██╗  ██╗ ██████╗  ██████╗ ██╗  ██╗███████╗
// ██║  ██║██╔═══██╗██╔═══██╗██║ ██╔╝██╔════╝
// ███████║██║   ██║██║   ██║█████╔╝ ███████╗
// ██╔══██║██║   ██║██║   ██║██╔═██╗ ╚════██║
// ██║  ██║╚██████╔╝╚██████╔╝██║  ██╗███████║
// ╚═╝  ╚═╝ ╚═════╝  ╚═════╝ ╚═╝  ╚═╝╚══════╝

export function useCheckTargetAccumulatorByTradeCapture() {
  return useContext(TargetAccumulatorActionContext).checkTargetAccumulator;
}

export function useTargetAccumulatorTileId() {
  const tileId = useContext(TargetAccumulatorTileIdContext);

  if (isNotDefined(tileId)) {
    throw new Error('Could not find tileId');
  }

  return tileId;
}

export function useTargetAccumulatorSelector<T>(
  selector: (targetAccumulator: FxTargetAccumulator, state: AppState) => T,
): T {
  const tileId = useTargetAccumulatorTileId();

  const selectorMemo = useCallback(
    (state: AppState): T => {
      const targetAccumulator = state.targetAccumulator[tileId];

      if (isNotDefined(targetAccumulator)) {
        throw new Error(`Could not find targetAccumulator with tileId ${tileId}`);
      }

      return selector(targetAccumulator, state);
    },
    [selector, tileId],
  );

  return useAppSelector(selectorMemo, deepEqual);
}
